import { useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { useParams } from 'react-router';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCurrentRole } from '@/hooks/use-current-role';
import { useOrg } from '@/components/org-layout';
import { useUpdateOrgName, useUpdateOrgProfile } from './org-organization-queries';
import { EmailInput } from '@/components/redacted-email';
import { FieldError } from '@/components/form/field-error';
import { PageContainer } from '@/components/page-container';

/**
 * Org profile select options (operator request 2026-06-13, GHL-style company
 * settings adapted to the HookMyApp audience: builders, agencies, vibecoders).
 * Stored as plain strings; picking "Other" reveals a free-text input and the
 * typed value is what gets stored.
 */
const BUSINESS_CATEGORIES = [
  'Software & SaaS',
  'Agency & Consulting',
  'E-commerce & Retail',
  'Education & Coaching',
  'Health & Wellness',
  'Real Estate',
  'Finance & Insurance',
  'Hospitality & Travel',
  'Media & Creators',
  'Other',
];

const BUSINESS_NICHES = [
  'AI Agents & Chatbots',
  'Customer Support',
  'Sales & CRM',
  'Marketing & Lead Generation',
  'Bookings & Scheduling',
  'E-commerce Operations',
  'Notifications & Alerts',
  'Developer Tools',
  'Other',
];

const PRIMARY_USE_CASES = [
  'Building a SaaS Product',
  'Agency Client Projects',
  'AI Agent Development',
  'Connecting WhatsApp to a CRM',
  'Internal Automation',
  'Testing & Prototyping',
  'Other',
];

/** Accepts bare domains too (example.com), mirroring the backend's IsUrl. */
function isWebsite(value: string): boolean {
  try {
    const url = new URL(value.includes('://') ? value : `https://${value}`);
    return url.hostname.includes('.');
  } catch {
    return false;
  }
}

/** Mirrors the backend DTO rules; every field also accepts '' (clears to NULL). */
const profileSchema = z.object({
  email: z
    .string()
    .max(254)
    .email('Enter a valid email address')
    .or(z.literal('')),
  phone: z
    .string()
    .max(32)
    .regex(/^\+?[\d\s().-]{7,20}$/, 'Enter a valid phone number')
    .or(z.literal('')),
  website: z
    .string()
    .max(254)
    .refine(isWebsite, 'Enter a valid website address')
    .or(z.literal('')),
});

type ProfileErrors = Partial<Record<'email' | 'phone' | 'website', string>>;

/** Validate a single field (on blur) so errors surface as you go, not only on Save. */
function fieldError(field: keyof ProfileErrors, value: string): string | undefined {
  const result = profileSchema.shape[field].safeParse(value.trim());
  return result.success ? undefined : result.error.issues[0]?.message;
}

function validateProfileField(field: keyof ProfileErrors) {
  return ({ value }: { value: string }) => fieldError(field, value);
}

const nameSchema = z.string().trim().min(1, 'Organization name is required');

/** Surface the AppError's userMessage (set from the response body) or the fallback. */
function errorMessage(err: unknown, fallback: string): string {
  return err && typeof err === 'object' && 'userMessage' in err
    ? ((err as { userMessage?: string }).userMessage ?? fallback)
    : fallback;
}

/** Select-or-free-text value, split for the "Other reveals an input" UX. */
interface OtherableValue {
  selected: string;
  otherText: string;
}

interface OrganizationFormValues {
  name: string;
  email: string;
  phone: string;
  website: string;
  businessCategory: OtherableValue;
  businessNiche: OtherableValue;
  primaryUseCase: OtherableValue;
}

function fromStored(value: string | null, options: string[]): OtherableValue {
  if (!value) return { selected: '', otherText: '' };
  if (options.includes(value)) return { selected: value, otherText: '' };
  return { selected: 'Other', otherText: value };
}

function toStored(v: OtherableValue): string {
  if (v.selected !== 'Other') return v.selected;
  return v.otherText.trim() || 'Other';
}

type LoadedOrg = NonNullable<ReturnType<typeof useOrg>['org']>;

function orgToFormValues(org: LoadedOrg): OrganizationFormValues {
  return {
    name: org.name ?? '',
    email: org.email ?? '',
    phone: org.phone ?? '',
    website: org.website ?? '',
    businessCategory: fromStored(org.businessCategory, BUSINESS_CATEGORIES),
    businessNiche: fromStored(org.businessNiche, BUSINESS_NICHES),
    primaryUseCase: fromStored(org.primaryUseCase, PRIMARY_USE_CASES),
  };
}

function hasProfileDiff(value: OrganizationFormValues, org: LoadedOrg) {
  return (
    value.email.trim() !== (org.email ?? '') ||
    value.phone.trim() !== (org.phone ?? '') ||
    value.website.trim() !== (org.website ?? '') ||
    toStored(value.businessCategory) !== (org.businessCategory ?? '') ||
    toStored(value.businessNiche) !== (org.businessNiche ?? '') ||
    toStored(value.primaryUseCase) !== (org.primaryUseCase ?? '')
  );
}

function SelectWithOther({
  id,
  label,
  shortLabel,
  options,
  value,
  onChange,
  disabled,
  className,
}: {
  id: string;
  label: string;
  /** Noun used in placeholders ("Select a business niche"). */
  shortLabel: string;
  options: string[];
  value: OtherableValue;
  onChange: (v: OtherableValue) => void;
  disabled: boolean;
  className?: string;
}) {
  return (
    <div className={`space-y-2 ${className ?? ''}`}>
      <Label htmlFor={id}>{label}</Label>
      <Select
        // base-ui only registers the SelectItems when the popup opens, so
        // `<Select.Value>` can't resolve the controlled value's label at mount.
        // `items` gives it the value->label map up front so the trigger shows
        // the selected label before the dropdown is ever opened.
        items={options.map((o) => ({ label: o, value: o }))}
        value={value.selected}
        onValueChange={(v) => onChange({ ...value, selected: v ?? '' })}
        disabled={disabled}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={`Select a ${shortLabel}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {value.selected === 'Other' && (
        <Input
          aria-label={`${label} (Other)`}
          placeholder={`Describe your ${shortLabel}`}
          value={value.otherText}
          onChange={(e) => onChange({ ...value, otherText: e.target.value })}
          disabled={disabled}
        />
      )}
    </div>
  );
}

/**
 * Organization page (spec Decision 2; operator rename 2026-06-12, was
 * "Company"): org identity + profile. Read for every org member (contract 3:
 * GET gate is org member); rename PATCHes /organizations/:orgId and the
 * profile form PATCHes /organizations/:orgId/profile, both org-admin only.
 * Only the name is required; every profile field is optional. Email, phone
 * and website are zod-validated client-side (the backend DTO enforces the
 * same rules server-side).
 */
export function OrgOrganizationPage() {
  const { org, refreshOrg } = useOrg();

  // Mount the form only once the org has loaded, keyed on its id, so `useForm`
  // is seeded with the org's real values as `defaultValues` (no reset-after-mount
  // hydration race — a form.reset() firing after the base-ui Select mounts left
  // the businessCategory field back on its empty default on ~half of loads).
  if (!org) {
    return (
      <div className="mx-auto flex h-full max-w-4xl items-center justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return <OrgOrganizationForm key={org.publicId} org={org} refreshOrg={refreshOrg} />;
}

function OrgOrganizationForm({
  org,
  refreshOrg,
}: {
  org: LoadedOrg;
  refreshOrg: () => Promise<void>;
}) {
  const { orgId } = useParams<{ orgId: string }>();
  const updateOrgName = useUpdateOrgName(orgId);
  const updateOrgProfile = useUpdateOrgProfile(orgId);
  const { role } = useCurrentRole();
  const canEdit = role === 'admin';
  const [saving, setSaving] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);

  // Name + profile share one dirty-gated Save; the name PATCHes
  // /organizations/:orgId and the profile PATCHes /organizations/:orgId/profile,
  // fired only for what changed. Defaults come straight from the loaded org.
  const form = useForm({
    defaultValues: orgToFormValues(org),
    onSubmit: async ({ value }) => {
      await handleSave(value);
    },
    onSubmitInvalid: ({ value }) => {
      if (nameTouched && value.name.trim() !== (org.name ?? '') && !value.name.trim()) {
        toast.error('Organization name is required');
      }
    },
  });

  // Reset every field back to the last-loaded org values (Cancel Changes).
  function reset() {
    form.reset(orgToFormValues(org));
    setNameTouched(false);
  }

  async function handleSave(value: OrganizationFormValues) {
    if (!orgId) return;
    const trimmedName = value.name.trim() || (!nameTouched ? (org.name ?? '') : '');
    const nameDirty = nameTouched && trimmedName !== (org.name ?? '');
    const profileDirty = hasProfileDiff(value, org);
    if (nameDirty && !trimmedName) {
      toast.error('Organization name is required');
      return;
    }
    // Only validate the profile fields when they're actually being saved — a
    // name-only save must not be blocked by stale/invalid legacy profile data.
    const parsed = profileDirty
      ? profileSchema.safeParse({
          email: value.email.trim(),
          phone: value.phone.trim(),
          website: value.website.trim(),
        })
      : null;
    if (parsed && !parsed.success) {
      return;
    }
    setSaving(true);
    try {
      // Two independent endpoints, fired only for what changed (name-only
      // saves must not touch the profile and vice versa). A failure on the
      // first stops the save, exactly as before.
      if (nameDirty) {
        try {
          await updateOrgName.mutateAsync({ name: trimmedName });
        } catch (err) {
          toast.error(errorMessage(err, 'Failed to update organization'));
          return;
        }
      }
      if (profileDirty && parsed?.success) {
        try {
          await updateOrgProfile.mutateAsync({
            ...parsed.data,
            businessCategory: toStored(value.businessCategory),
            businessNiche: toStored(value.businessNiche),
            primaryUseCase: toStored(value.primaryUseCase),
          });
        } catch (err) {
          toast.error(errorMessage(err, 'Failed to update organization'));
          return;
        }
      }
      toast.success('Organization updated');
      await refreshOrg();
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageContainer variant="fill" className="gap-4">
      <h1 className="text-xl font-semibold">Organization</h1>
      <Card>
        <CardContent className="space-y-4">
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void form.handleSubmit();
            }}
            className="space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <form.Field
                name="name"
                validators={{
                  onSubmit: ({ value }) => {
                    if (!nameTouched && org?.name) return undefined;
                    const result = nameSchema.safeParse(value);
                    return result.success ? undefined : result.error.issues[0]?.message;
                  },
                }}
              >
                {(field) => {
                  const hasError = field.state.meta.errors.length > 0;
                  return (
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="org-name">Organization Name</Label>
                      <Input
                        id="org-name"
                        value={
                          field.state.value || (!nameTouched ? (org?.name ?? '') : '')
                        }
                        onChange={(e) => {
                          setNameTouched(true);
                          field.handleChange(e.target.value);
                        }}
                        onBlur={field.handleBlur}
                        aria-invalid={hasError || undefined}
                        aria-describedby={hasError ? 'org-name-error' : undefined}
                        disabled={!canEdit}
                      />
                      <FieldError id="org-name-error" errors={field.state.meta.errors} />
                    </div>
                  );
                }}
              </form.Field>
              <form.Field
                name="email"
                validators={{
                  onBlur: validateProfileField('email'),
                  onSubmit: validateProfileField('email'),
                }}
              >
                {(field) => {
                  const hasError = field.state.meta.errors.length > 0;
                  return (
                    <div className="space-y-2">
                      <Label htmlFor="org-email">Organization Email</Label>
                      <EmailInput
                        id="org-email"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        aria-invalid={hasError || undefined}
                        aria-describedby={hasError ? 'org-email-error' : undefined}
                        disabled={!canEdit}
                      />
                      <FieldError id="org-email-error" errors={field.state.meta.errors} />
                    </div>
                  );
                }}
              </form.Field>
              <form.Field
                name="phone"
                validators={{
                  onBlur: validateProfileField('phone'),
                  onSubmit: validateProfileField('phone'),
                }}
              >
                {(field) => {
                  const hasError = field.state.meta.errors.length > 0;
                  return (
                    <div className="space-y-2">
                      <Label htmlFor="org-phone">Organization Phone</Label>
                      <Input
                        id="org-phone"
                        type="tel"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        placeholder="+1 555 000 0000"
                        aria-invalid={hasError || undefined}
                        aria-describedby={hasError ? 'org-phone-error' : undefined}
                        disabled={!canEdit}
                      />
                      <FieldError id="org-phone-error" errors={field.state.meta.errors} />
                    </div>
                  );
                }}
              </form.Field>
              <form.Field
                name="website"
                validators={{
                  onBlur: validateProfileField('website'),
                  onSubmit: validateProfileField('website'),
                }}
              >
                {(field) => {
                  const hasError = field.state.meta.errors.length > 0;
                  return (
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="org-website">Organization Website</Label>
                      <Input
                        id="org-website"
                        type="url"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        placeholder="https://example.com"
                        aria-invalid={hasError || undefined}
                        aria-describedby={hasError ? 'org-website-error' : undefined}
                        disabled={!canEdit}
                      />
                      <FieldError id="org-website-error" errors={field.state.meta.errors} />
                    </div>
                  );
                }}
              </form.Field>
              <form.Field name="businessCategory">
                {(field) => (
                  <SelectWithOther
                    id="org-business-category"
                    shortLabel="business category"
                    label="Business Category"
                    options={BUSINESS_CATEGORIES}
                    value={field.state.value}
                    onChange={field.handleChange}
                    disabled={!canEdit}
                  />
                )}
              </form.Field>
              <form.Field name="businessNiche">
                {(field) => (
                  <SelectWithOther
                    id="org-business-niche"
                    shortLabel="business niche"
                    label="Business Niche"
                    options={BUSINESS_NICHES}
                    value={field.state.value}
                    onChange={field.handleChange}
                    disabled={!canEdit}
                  />
                )}
              </form.Field>
              <form.Field name="primaryUseCase">
                {(field) => (
                  <SelectWithOther
                    id="org-primary-use-case"
                    shortLabel="use case"
                    label="What do you primarily use HookMyApp for?"
                    options={PRIMARY_USE_CASES}
                    value={field.state.value}
                    onChange={field.handleChange}
                    disabled={!canEdit}
                    className="sm:col-span-2"
                  />
                )}
              </form.Field>
            </div>
            <form.Subscribe selector={(state) => state.values}>
              {(value) => {
                const displayName = value.name.trim() || (!nameTouched ? (org?.name ?? '') : '');
                const nameDirty = !!org && nameTouched && displayName !== (org.name ?? '');
                const profileDirty = !!org && hasProfileDiff(value, org);
                const isDirty = nameDirty || profileDirty;

                if (!canEdit || !isDirty) return null;

                return (
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={reset}
                      disabled={saving}
                    >
                      Cancel Changes
                    </Button>
                    <Button type="submit" disabled={saving}>
                      {saving && <Loader2 className="size-3.5 animate-spin" />}
                      Save Changes
                    </Button>
                  </div>
                );
              }}
            </form.Subscribe>
          </form>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
